"use client";

import { useEffect, useRef, useState } from "react";
import { Box, Spinner, Heading, Flex, Tooltip } from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import axios from "axios";
import echarts from "./lib";

// Forecast & Inventory section — GREEN secondary (accent #12775F, brightened;
// switched from amber 2026-08-29, which clashed with Sales Trends). Emphasis
// is a brighter self-tone + shadow rather than a hue swap — teal reads too
// close to this green for a hover flip.
const GREEN = "#1AA47C";
const GREEN_DEEP = "#12775F";

const lighten = (hex, amt) => {
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift) => {
    const c = (n >> shift) & 0xff;
    return Math.round(c + (255 - c) * amt);
  };
  return `rgb(${ch(16)}, ${ch(8)}, ${ch(0)})`;
};

const barGradient = (from, to) => ({
  type: "linear",
  x: 0,
  y: 0,
  x2: 1,
  y2: 0,
  colorStops: [
    { offset: 0, color: from },
    { offset: 1, color: to },
  ],
});

export default function Chart7({ supplier, retailer }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [height, setHeight] = useState("300px");
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    let disposed = false;

    const initChart = async () => {
      if (!containerRef.current) return;
      setLoading(true);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/rep_sales/chart7`,
          {
            supplier,
            retailer,
            userid: localStorage.getItem("userid"),
          }
        );

        // Identical transform to the AmCharts version — the DATA must not
        // change: Number(), drop NaN/<=0, pct = round(value*1000)/10.
        const raw = Array.isArray(res.data?.data) ? res.data.data : [];
        const data = raw
          .map((r) => {
            const value = Number(r?.value);
            if (isNaN(value) || value <= 0) return null;
            const pct = Math.round(value * 1000) / 10;
            return {
              region: r.region || "Unknown",
              value: pct,
              percentLabel: `${pct.toFixed(1)} %`,
            };
          })
          .filter(Boolean);

        if (disposed) return;

        if (!data.length) {
          setNoData(true);
          return;
        }
        setNoData(false);
        setHeight(`${Math.max(data.length * 50, 300)}px`);

        if (!containerRef.current) return;
        const chart = echarts.init(containerRef.current);
        chartRef.current = chart;

        let sortedByValue = false;

        const render = () => {
          const rows = sortedByValue
            ? [...data].sort((a, b) => b.value - a.value)
            : data;

          chart.setOption(
            {
              title: {
                subtext: sortedByValue
                  ? "sorted — click a bar for original order"
                  : "click a bar to sort by value",
                right: 0,
                top: 0,
                subtextStyle: { color: "#93A5B1", fontSize: 10.5 },
              },
              grid: { left: 8, right: 48, top: 20, bottom: 4, containLabel: true },
              xAxis: {
                type: "value",
                min: 0,
                max: 100,
                axisLabel: { color: "#64798A", fontSize: 11, formatter: (v) => `${v}%` },
                splitLine: { lineStyle: { color: "#E2E9EE", type: [3, 3] } },
                axisLine: { show: false },
                axisTick: { show: false },
              },
              yAxis: {
                type: "category",
                inverse: true,
                data: rows.map((r) => r.region),
                axisLabel: {
                  color: "#4A5D6B",
                  fontSize: 12,
                  fontFamily: "Inter, sans-serif",
                  width: 120,
                  overflow: "break",
                },
                axisLine: { show: false },
                axisTick: { show: false },
              },
              tooltip: {
                trigger: "axis",
                axisPointer: { type: "shadow", shadowStyle: { color: "rgba(26,164,124,0.07)" } },
                confine: true,
                backgroundColor: "#ffffff",
                borderColor: "#E2E9EE",
                borderWidth: 1,
                padding: [8, 12],
                textStyle: { color: "#1D2F3A", fontSize: 12 },
                extraCssText: "box-shadow: 0 6px 18px rgba(29,47,58,0.14); border-radius: 8px;",
                formatter: (params) => {
                  const p = params[0];
                  return `${p.name}<br/><b>${Number(p.value).toFixed(1)} %</b> of opening stock sold`;
                },
              },
              series: [
                {
                  type: "bar",
                  barWidth: "68%",
                  showBackground: true,
                  backgroundStyle: { color: "#F6F8FA", borderRadius: [0, 6, 6, 0] },
                  data: rows.map((r) => {
                    const inside = r.value >= 70; // scale is fixed 0-100
                    return {
                      value: r.value,
                      label: {
                        show: true,
                        position: inside ? "insideRight" : "right",
                        distance: 8,
                        color: inside ? "#ffffff" : "#4A5D6B",
                        ...(inside
                          ? { textShadowColor: "rgba(0,0,0,0.28)", textShadowBlur: 3 }
                          : {}),
                        fontSize: 12,
                        fontWeight: 700,
                        fontFamily: "Inter, sans-serif",
                        formatter: (p) => `${Number(p.value).toFixed(1)} %`,
                      },
                    };
                  }),
                  itemStyle: {
                    borderRadius: [0, 6, 6, 0],
                    color: barGradient(lighten(GREEN, 0.35), GREEN_DEEP),
                  },
                  emphasis: {
                    itemStyle: {
                      color: barGradient(lighten(GREEN, 0.15), GREEN),
                      shadowBlur: 10,
                      shadowColor: "rgba(29,47,58,0.22)",
                    },
                  },
                  animationDuration: 900,
                  animationDelay: (idx) => idx * 70,
                  animationEasing: "cubicOut",
                  animationDurationUpdate: 500,
                },
              ],
            },
            { replaceMerge: ["series"] }
          );
        };

        render();
        chart.on("click", { seriesIndex: 0 }, () => {
          sortedByValue = !sortedByValue;
          render();
        });
      } catch (err) {
        console.error("Chart7 fetch", err);
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    initChart();

    const ro = new ResizeObserver(() => {
      if (chartRef.current) chartRef.current.resize();
    });
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      disposed = true;
      ro.disconnect();
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [supplier, retailer]);

  return (
    <Box height={height} display="flex" flexDirection="column" position="relative">
      <Flex justify="space-between" align="center" mb={2}>
        <Heading size="xs" color="gray.600" fontWeight="600">Sell-Through % by Region</Heading>
        <Tooltip label="Percentage of this month’s opening stock that has been sold so far." placement="left" hasArrow>
          <InfoIcon w={3.5} h={3.5} cursor="pointer" color="gray.400" />
        </Tooltip>
      </Flex>
      <Box flex="1" position="relative">
        {noData && (
          <Flex align="center" justify="center" position="absolute" top="0" left="0" width="100%" height="100%" bg="whiteAlpha.800" zIndex="1">
            <Heading size="sm" color="gray.600">
              No data available
            </Heading>
          </Flex>
        )}
        <div ref={containerRef} style={{ width: "100%", height: "100%", overflow: "hidden" }} />
        {loading && <Spinner position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" />}
      </Box>
    </Box>
  );
}
